import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Shield, ShieldCheck, FileText, BookOpen, Database, DollarSign,
  LogIn, LogOut, Menu, X,
} from 'lucide-react';

/**
 * PublicShell — one mobile-aware outer shell used by every *public* page
 * (LandingPage, PublicPricingPage, NewDocumentationPage, NewUseCasesPage,
 * SourcesPage, ProvenancePage).
 *
 * Desktop (≥lg): fixed left rail, identical to the pre-existing sidebars.
 * Mobile (<lg): compact header with brand + hamburger. Tap the hamburger to
 * open a full-height slide-in drawer with the same nav items. Close on item
 * click, on backdrop click, or with the X button.
 *
 * Content is rendered via children and receives mobile-safe horizontal
 * padding; pages can still add their own inner padding / max-width.
 */

const NAV = [
  { path: '/',              icon: Home,        label: 'Home',                  color: '#0ea5e9' },
  { path: '/platform',      icon: Shield,      label: 'Intelligence Platform', auth: true, color: '#16a34a' },
  { path: '/documentation', icon: FileText,    label: 'Documentation',         color: '#2563eb' },
  { path: '/use-cases',     icon: BookOpen,    label: 'Use Cases',             color: '#9333ea' },
  { path: '/sources',       icon: Database,    label: 'Research Database',     color: '#0891b2' },
  { path: '/provenance',    icon: ShieldCheck, label: 'Data Provenance',       color: '#059669' },
  { path: '/pricing',       icon: DollarSign,  label: 'Pricing',               color: '#ca8a04' },
];

function NavLinks({ user, onItemClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const go = (path, auth) => {
    if (auth && !user) navigate('/login');
    else navigate(path);
    if (onItemClick) onItemClick();
  };
  const isActive = (p) => {
    if (p === '/') return location.pathname === '/';
    if (p === '/provenance') return location.pathname.startsWith('/provenance');
    return location.pathname === p;
  };
  return (
    <nav className="space-y-1">
      {NAV.map(item => {
        const Icon = item.icon;
        const active = isActive(item.path);
        return (
          <div key={item.path} onClick={() => go(item.path, item.auth)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer ${
              active
                ? 'bg-[#e5eeff] text-[#004ac6] font-semibold'
                : 'text-[#434655] hover:text-[#004ac6] hover:bg-[#eff4ff]'
            }`}>
            <Icon className="w-[18px] h-[18px] shrink-0" style={active ? undefined : { color: item.color }} />
            <span className="truncate tracking-tight">{item.label}</span>
          </div>
        );
      })}
    </nav>
  );
}

export default function PublicShell({ user, onLogout, children, contentClass = '' }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  // Close the drawer whenever the viewport crosses into desktop territory so
  // users don't end up with a hidden overlay state lingering.
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setDrawerOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Lock body scroll while the drawer is open so the page underneath doesn't
  // slide around under the finger.
  useEffect(() => {
    if (drawerOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  return (
    <div className="flex min-h-screen bg-[#f8f9ff]">
      {/* ─── Desktop sidebar (≥lg) ─── */}
      <aside className="hidden lg:flex w-60 shrink-0 bg-white border-r border-[#c3c6d7]/40 sticky top-0 h-screen flex-col px-3 py-5"
             data-testid="public-shell-desktop-sidebar">
        <div onClick={() => navigate('/')} className="px-3 mb-6 cursor-pointer">
          <div className="text-[15px] font-extrabold tracking-tight text-[#0b1c30]">Sovereign IQ</div>
          <div className="text-[10px] uppercase tracking-widest text-[#737686]">Intelligence Platform</div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavLinks user={user} />
        </div>
        <div className="pt-4 mt-auto border-t border-[#c3c6d7]/30 space-y-1">
          {user ? (
            <>
              <div className="px-3 py-1 text-[11px] text-[#737686] truncate">{user.email}</div>
              <div onClick={onLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#434655] hover:text-red-600 hover:bg-red-50 cursor-pointer">
                <LogOut className="w-[18px] h-[18px] shrink-0" /><span>Log out</span>
              </div>
            </>
          ) : (
            <div onClick={() => navigate('/login')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#004ac6] hover:bg-[#eff4ff] cursor-pointer font-semibold">
              <LogIn className="w-[18px] h-[18px] shrink-0" /><span>Sign in</span>
            </div>
          )}
        </div>
      </aside>

      {/* ─── Mobile header + content wrapper ─── */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-white border-b border-[#c3c6d7]/40 shadow-sm"
                data-testid="public-shell-mobile-header">
          <div onClick={() => navigate('/')} className="cursor-pointer">
            <div className="text-[14px] font-extrabold tracking-tight text-[#0b1c30]">Sovereign IQ</div>
            <div className="text-[9px] uppercase tracking-widest text-[#737686]">Intelligence Platform</div>
          </div>
          <div className="flex items-center gap-2">
            {!user && (
              <button onClick={() => navigate('/login')}
                className="text-xs font-bold text-[#004ac6] px-2 py-1.5 hover:bg-[#eff4ff] rounded-md">
                Sign in
              </button>
            )}
            <button onClick={() => setDrawerOpen(true)} data-testid="public-shell-hamburger"
                    aria-label="Open menu"
                    className="p-2 -mr-2 rounded-lg hover:bg-[#eff4ff] active:scale-95 transition-all">
              <Menu className="w-5 h-5 text-[#0b1c30]" />
            </button>
          </div>
        </header>

        <main className={`flex-1 min-w-0 ${contentClass}`}>
          {children}
        </main>
      </div>

      {/* ─── Mobile drawer (<lg) ─── */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="public-shell-mobile-drawer">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in"
               onClick={() => setDrawerOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-[82vw] max-w-[320px] bg-white flex flex-col px-3 py-5 shadow-2xl animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between px-3 mb-6">
              <div onClick={() => { navigate('/'); setDrawerOpen(false); }} className="cursor-pointer">
                <div className="text-[15px] font-extrabold tracking-tight text-[#0b1c30]">Sovereign IQ</div>
                <div className="text-[10px] uppercase tracking-widest text-[#737686]">Intelligence Platform</div>
              </div>
              <button onClick={() => setDrawerOpen(false)} data-testid="public-shell-close-drawer"
                      aria-label="Close menu"
                      className="p-2 -mr-1 rounded-lg hover:bg-[#eff4ff] active:scale-95 transition-all">
                <X className="w-5 h-5 text-[#0b1c30]" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <NavLinks user={user} onItemClick={() => setDrawerOpen(false)} />
            </div>
            <div className="pt-4 mt-auto border-t border-[#c3c6d7]/30 space-y-1">
              {user ? (
                <>
                  <div className="px-3 py-1 text-[11px] text-[#737686] truncate">{user.email}</div>
                  <div onClick={() => { onLogout?.(); setDrawerOpen(false); }}
                       className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#434655] hover:text-red-600 hover:bg-red-50 cursor-pointer">
                    <LogOut className="w-[18px] h-[18px] shrink-0" /><span>Log out</span>
                  </div>
                </>
              ) : (
                <div onClick={() => { navigate('/login'); setDrawerOpen(false); }}
                     className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#004ac6] hover:bg-[#eff4ff] cursor-pointer font-semibold">
                  <LogIn className="w-[18px] h-[18px] shrink-0" /><span>Sign in</span>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
